<script setup>
import { computed, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const userAccounts = useMapGetter('getUserAccounts');

const isOpen = ref(false);
const searchQuery = ref('');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const filteredAccounts = computed(() => {
  if (!searchQuery.value.trim()) {
    return sortedCurrentUserAccounts.value;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return sortedCurrentUserAccounts.value.filter(account => {
    const accountName = account.name.toLowerCase();
    const accountRole = account.custom_role_id
      ? account.custom_role.name.toLowerCase()
      : account.role.toLowerCase();

    return accountName.includes(query) || accountRole.includes(query);
  });
});

const toggle = () => {
  if (!showAccountSwitcher.value) return;
  isOpen.value = !isOpen.value;
  if (!isOpen.value) searchQuery.value = '';
};

const closeMenu = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

const onChangeAccount = newId => {
  closeMenu();
  window.location.href = `/app/accounts/${newId}/dashboard`;
};

const emitNewAccount = () => {
  closeMenu();
  emit('showCreateAccountModal');
};
</script>

<template>
  <div
    v-on-click-outside="closeMenu"
    class="relative w-full font-sans"
  >
    <button
      id="sidebar-account-switcher"
      :data-account-id="accountId"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      class="relative flex items-center gap-2 w-full h-10 rounded-lg ps-9 pe-3 border border-solid bg-white dark:bg-n-solid-3 transition-colors text-start border-n-weak"
      :class="[
        showAccountSwitcher ? 'cursor-pointer hover:border-[var(--sidebar-accent)]' : 'cursor-default',
        isOpen && 'border-[var(--sidebar-accent)]',
      ]"
      @click="toggle"
    >
      <span
        aria-hidden="true"
        class="i-lucide-repeat absolute start-3 top-1/2 -translate-y-1/2 size-4 flex-shrink-0 text-n-slate-11"
      />
      <span
        class="flex-1 min-w-0 text-[14px] font-semibold leading-5 truncate text-n-slate-12"
        aria-live="polite"
        :title="currentAccount.name"
      >
        {{ currentAccount.name }}
      </span>
      <span
        v-if="showAccountSwitcher"
        aria-hidden="true"
        class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-11"
      />
    </button>

    <div
      v-if="isOpen && showAccountSwitcher"
      class="absolute start-0 top-full z-20 mt-1 w-80 min-w-[20rem]"
    >
      <div
        class="rounded-lg border border-n-weak bg-white dark:bg-n-solid-3 shadow-md py-2"
      >
        <p class="px-3 mb-2 text-xs font-medium text-n-slate-11">
          {{ t('SIDEBAR_ITEMS.SWITCH_ACCOUNT') }}
        </p>

        <div class="px-2 mb-2">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('SIDEBAR_ITEMS.SEARCH_ACCOUNTS')"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:border-[var(--sidebar-accent)]"
            @click.stop
          />
        </div>

        <div class="max-h-80 overflow-y-auto px-1">
          <p
            v-if="filteredAccounts.length === 0"
            class="px-3 py-4 text-center text-sm text-n-slate-11"
          >
            {{ t('SIDEBAR_ITEMS.NO_ACCOUNTS_FOUND') }}
          </p>
          <button
            v-for="account in filteredAccounts"
            :id="`account-${account.id}`"
            :key="account.id"
            type="button"
            class="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-left hover:bg-[var(--sidebar-hover-bg)]"
            @click="onChangeAccount(account.id)"
          >
            <span
              class="min-w-0 flex-1 text-sm text-n-slate-12 break-words"
              :title="account.name"
            >
              {{ account.name }}
            </span>
            <span
              class="flex-shrink-0 text-xs capitalize text-n-slate-11 whitespace-nowrap"
            >
              {{
                account.custom_role_id
                  ? account.custom_role.name
                  : account.role
              }}
            </span>
            <Icon
              v-show="account.id === accountId"
              icon="i-lucide-check"
              class="size-4 flex-shrink-0 text-[var(--sidebar-accent)]"
            />
          </button>
        </div>

        <div
          v-if="globalConfig.createNewAccountFromDashboard"
          class="px-2 pt-2 mt-1 border-t border-n-weak"
        >
          <ButtonNext
            color="slate"
            variant="faded"
            class="w-full"
            size="sm"
            @click="emitNewAccount"
          >
            {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
          </ButtonNext>
        </div>
      </div>
    </div>
  </div>
</template>
